import { ThrowLiteError } from '../lite-error.js';
import { stopEngine, _getAdapterOptions, resizeEngine, startEngine } from './engine.js';
import { disposeGpuResourceRetirements } from './gpu-resource-retirement.js';
import { TU } from './gpu-flags.js';
import { _refreshScRT } from './surface.js';

async function runDeviceLostRecovery(engine, state, registrations) {
  const handlers = /* @__PURE__ */ new Map();
  for (const registration of registrations) {
    handlers.set(registration._kind, registration);
  }
  const wasRunning = engine._renderFn !== null;
  stopEngine(engine);
  assertEveryActiveContextKindIsRecoverable(engine, handlers);
  disposeGpuResourceRetirements(engine);
  const adapter = await runRecoveryStep("requesting a replacement adapter", () => navigator.gpu.requestAdapter({ powerPreference: "high-performance", ..._getAdapterOptions() }));
  if (!adapter) {
    ThrowLiteError(43);
  }
  const missingFeatures = state._requiredFeatures.filter((feature) => !adapter.features.has(feature));
  if (missingFeatures.length) {
    ThrowLiteError(44, missingFeatures.join(", "));
  }
  engine._device = await runRecoveryStep(
    "requesting a replacement device",
    () => adapter.requestDevice({
      requiredFeatures: state._requiredFeatures,
      requiredLimits: { ...engine._options?.requiredLimits, ...engine._storageRequiredLimits }
    })
  );
  await runRecoveryStep("rebuilding engine storage buffers", () => engine._rebuildStorageBuffers?.());
  await runRecoveryStep("reconfiguring rendering surfaces", () => {
    for (const surface of engine.surfaces) {
      const usage = surface._swapchainCopySrc ? TU.RENDER_ATTACHMENT | TU.COPY_SRC : TU.RENDER_ATTACHMENT;
      surface._context.configure({
        device: engine._device,
        format: surface._configureFormat,
        alphaMode: surface._alphaMode,
        usage,
        viewFormats: [surface.format]
      });
      _refreshScRT(surface);
    }
  });
  await runRecoveryStep("resizing rendering surfaces", () => resizeEngine(engine));
  const settleTextureOwnership = await rebuildRecoverableTextures(engine, state);
  const orderedHandlers = Array.from(handlers.values()).sort((a, b) => (a._recoverOrder ?? 0) - (b._recoverOrder ?? 0));
  try {
    for (const handler of orderedHandlers) {
      await runRecoveryStep(`running "${handler._kind}" recovery`, () => handler._recover(engine));
    }
  } finally {
    settleTextureOwnership?.();
  }
  if (wasRunning) {
    await runRecoveryStep("restarting rendering", () => startEngine(engine));
  }
}
async function rebuildRecoverableTextures(engine, state) {
  const tracked = state._textures;
  const textures = [];
  for (const ref of tracked) {
    const texture = ref.deref();
    if (texture) {
      textures.push(texture);
    } else {
      tracked.delete(ref);
    }
  }
  if (textures.length === 0) {
    return void 0;
  }
  const { rebuildTexture2D, settleRebuiltTextureOwnership } = await import('../texture/texture-recovery.js');
  try {
    await runRecoveryStep("rebuilding recoverable textures", async () => {
      const results = await Promise.allSettled(textures.map((texture) => rebuildTexture2D(engine, texture)));
      const failed = results.find((result) => result.status === "rejected");
      if (failed) {
        throw failed.reason;
      }
    });
  } catch (error) {
    settleRebuiltTextureOwnership(state);
    throw error;
  }
  return () => settleRebuiltTextureOwnership(state);
}
function assertEveryActiveContextKindIsRecoverable(engine, handlers) {
  const unrecoverable = /* @__PURE__ */ new Set();
  for (const surface of engine.surfaces) {
    for (const context of surface._renderingContexts) {
      if (!handlers.has(context._kind)) {
        unrecoverable.add(context._kind);
      }
    }
  }
  if (unrecoverable.size) {
    throw new Error(
      `Device-lost recovery cannot rebuild registered rendering contexts of kind: ${Array.from(unrecoverable).sort().join(", ")}. Recovering around them would leave them bound to the lost device and crash the browser's renderer process on the next frame. Enable that kind's device-lost recovery before the device is lost, or unregister the context.`
    );
  }
}
async function runRecoveryStep(description, action) {
  try {
    return await action();
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`Device-lost recovery failed while ${description}: ${message}`, { cause: error });
  }
}

export { runDeviceLostRecovery };
//# sourceMappingURL=device-lost-recovery-run.js.map
